// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { useGetReportingDashboardQuery, GetReportingDashboardQuery } from '../queries/getReportingDashboard.query.generated';

import { css } from '@emotion/css';
import { useTheme, Theme, LinearProgress, Button, Alert } from '@mui/material';

import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Spacer from '@components/Utils/Spacer';
import Toolbar from '@components/Utils/Toolbar';
import { useShellTabActions } from '@features/products/shell/shellTabActions';

import { REPORTING_MOVE_LIMIT, ReportingBusinessHours, ReportingMove } from '../types';
import { useReporting } from '../providers/ReportingProvider';
import ReportingControls from './ReportingControls';
import ReportingTabs from './ReportingTabs';
import OperationsOverviewTab from './OperationsOverviewTab';
import AppointmentUsageTab from './AppointmentUsageTab';
import ExecutiveKpisTab from './ExecutiveKpisTab';

// TYPES ---------------------------------------------------------------- //

export type ReportingQueryMoves = GetReportingDashboardQuery['moves'];
export type ReportingQueryCustomer = GetReportingDashboardQuery['customer'];

interface ReportingDashboardContentProps {
  /** The shell band and tab strip already name the surface, so the title row and its header band drop. */
  embedded?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const ReportingDashboardContent = ({ embedded = false }: ReportingDashboardContentProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { customerId, start, end, selectedTab, appointmentConfig } = useReporting();

  const { data, loading, error, refetch } = useGetReportingDashboardQuery({
    variables: {
      start,
      end,
      customerId,
      limit: REPORTING_MOVE_LIMIT,
    },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const moves = React.useMemo(() => data?.moves || [], [data?.moves]);
  const customer = data?.customer;
  const businessHours = React.useMemo(() => customer?.location?.businesshours || [], [customer?.location?.businesshours]);
  const totalMoveCount = data?.movesAggregate?.aggregate?.count ?? 0;
  const truncated = totalMoveCount > REPORTING_MOVE_LIMIT;

  // Keep the loading bar visible briefly to avoid flicker on fast responses
  const [loadingVisible, setLoadingVisible] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (loading) {
      setLoadingVisible(true);
    } else {
      setTimeout(() => setLoadingVisible(false), 250);
    }
  }, [loading]);

  const handleRefetch = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Outside a layout this is a no-op.
  const tabActions = React.useMemo(() => (embedded ? { sync: handleRefetch } : null), [embedded, handleRefetch]);
  useShellTabActions(tabActions);

  const isEmpty = !loading && !error && moves.length === 0;

  const filters = (
    <>
      <ReportingControls />

      <Spacer size={2} />

      <ReportingTabs />
    </>
  );

  return (
    <div className={cls.page}>
      {embedded ? (
        <div className={cls.embeddedHeader}>{filters}</div>
      ) : (
        <div className={cls.header}>
          <Toolbar fullscreen zIndex={0} position='relative' title='Reports' refetch={handleRefetch} />

          <div className={cls.headerDetails}>{filters}</div>
        </div>
      )}

      {loadingVisible ? <LinearProgress /> : <div className={cls.loadingPad} />}

      <div className={cls.body}>
        {error ? (
          <div className={cls.errorBox}>
            <DefaultFallback severity='error' message='Failed to fetch reporting data.' />
            <Spacer size={2} />
            <Button variant='outlined' color='primary' onClick={handleRefetch}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            {truncated ? (
              <>
                <Alert severity='warning'>
                  Large result set. Some charts are based on the first {REPORTING_MOVE_LIMIT.toLocaleString()} moves.
                  Narrow the date range for full detail.
                </Alert>
                <Spacer size={2} />
              </>
            ) : null}

            {isEmpty ? (
              <>
                <DefaultFallback severity='info' message='No move data for this date range.' />
                <Spacer size={2} />
              </>
            ) : null}

            {selectedTab === 'operations' ? (
              <OperationsOverviewTab
                moves={moves as ReportingMove[]}
                start={start}
                end={end}
                loading={loading && !data}
              />
            ) : null}

            {selectedTab === 'appointments' ? (
              <AppointmentUsageTab
                moves={moves as ReportingMove[]}
                businessHours={businessHours as ReportingBusinessHours[]}
                appointmentConfig={appointmentConfig}
                start={start}
                end={end}
                loading={loading && !data}
              />
            ) : null}

            {selectedTab === 'executive' ? (
              <ExecutiveKpisTab moves={moves as ReportingMove[]} start={start} end={end} />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => {
  return {
    page: css`
      position: relative;
      width: 100%;
      min-height: 100%;
      background-color: ${theme?.palette?.background?.default};
    `,
    header: css`
      background-color: ${theme?.palette?.background?.paper};
      border-bottom: 1px solid ${theme?.palette?.divider};
    `,
    headerDetails: css`
      padding: ${theme?.spacing(0, 2, 0, 2)};
    `,
    // Inside the Valet shell there is no header band above the controls, so this adds its own top inset.
    embeddedHeader: css`
      padding: ${theme?.spacing(3, 2, 0, 2)};
    `,
    loadingPad: css`
      width: 100%;
      height: 4px;
    `,
    body: css`
      padding: ${theme?.spacing(2)};
    `,
    errorBox: css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default ReportingDashboardContent;
